import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

export interface MentalWellnessData {
  date: string;
  mental_wellness_score: number;
  mood_score: number;
  emotion_score: number;
  thought_score: number;
  coping_score: number;
  energy_score: number;
  mood_rating: number;
  energy_level: number;
  mind_clarity: number;
  spiritual_connection: number;
  emotions: string[];
  thought_patterns: string[];
  coping_behaviors: string[];
}

export function useMentalWellnessScore(days: number = 30) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['mental-wellness-scores', days],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');
      
      const { data: mwData, error } = await supabase
        .from('mental_wellness_daily' as any)
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (error) throw error;
      return mwData as any as MentalWellnessData[];
    }
  });

  const computeScore = useMutation({
    mutationFn: async (date?: string) => {
      const { data, error } = await supabase.functions.invoke('compute-mental-wellness', {
        body: { date: date || format(new Date(), 'yyyy-MM-dd') }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mental-wellness-scores'] });
    }
  });

  const todayScore = data?.[0];
  const sevenDayAvg = data && data.length >= 7 
    ? data.slice(0, 7).reduce((sum, d) => sum + d.mental_wellness_score, 0) / 7 
    : null;
  const thirtyDayAvg = data && data.length >= 30 
    ? data.slice(0, 30).reduce((sum, d) => sum + d.mental_wellness_score, 0) / 30 
    : null;

  return {
    data,
    isLoading,
    todayScore,
    sevenDayAvg,
    thirtyDayAvg,
    computeScore: computeScore.mutate,
    isComputing: computeScore.isPending
  };
}
